import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import PhoenixImage from "../components/PhoenixImage";
import AmbientPlayer from "../components/AmbientPlayer";
import { ToolkitSongCard, PriceTag, LaunchRibbon } from "../components/ToolkitSongCard";
import { BundleBuilder } from "../components/BundleBuilder";
import { SessionBooking } from "../components/SessionBooking";
import { Testimonials } from "../components/Testimonials";
import { LaunchBanner } from "../components/LaunchBanner";
import { RatingBadge } from "../components/RatingBadge";
import { StartQuiz } from "../components/StartQuiz";
import { RecoveryMoments } from "../components/RecoveryMoments";
import { Phone, ArrowRight, Play, ShoppingBag } from "lucide-react";
import { addToBundle } from "../components/BundleBuilder";

const BEFORE_PHOTO = "https://customer-assets-jt897jd0.emergentagent.net/job_why-recover-app/artifacts/upydm72w_7E38F12E-63BD-4B0F-B285-F1AA8E7E3499.png";
const AFTER_PHOTO = "https://customer-assets-7cd3h4nn.emergentagent.net/wingman/5af77ba5-af76-4e7d-a867-09ea45f0ab28/attachments/e00683b60f194542a00b9c0b30f7d20f_0B445525-E394-41DA-9F8F-10E0D675C4B8.png";

const AMBIENT_TRACKS = [
  { id: "f3OXFL5rETE", label: "Dean Lewis — Smells Like Teen Spirit" },
  { id: "byvvFnzUez0", label: "Cary Brothers — Ride" },
];

const PATHS = [
  ["addiction", "Addiction", "free-downloads", "border-teal-400/40 hover:bg-teal-500/15 text-teal-100"],
  ["mental-health", "Mental Health", "card-mental-health", "border-amber-400/40 hover:bg-amber-500/15 text-amber-100"],
  ["hardship", "Hardship", "card-hardship", "border-white/40 hover:bg-white/10 text-white"],
];

const goTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "center" });

const BEYOND_TOOLKITS = [
  ["mental-health", {
    shopId: "finding_your_footing",
    kicker: "Finding Your Footing · Mental Health",
    song: "1-800-273-8255",
    artist: "Logic ft. Alessia Cara & Khalid",
    album: "Everybody",
    cover: "https://archive.org/download/mbid-e564d1eb-ac43-464d-bf64-286cb3e36cdb/mbid-e564d1eb-ac43-464d-bf64-286cb3e36cdb-17685788284_thumb500.jpg",
    coverAlt: "Logic — Everybody album cover",
    blurb: "For anxiety, depression, and the noise in your head that no one else can hear. A workbook for naming what you feel and finding your footing.",
    inside: "naming the feeling · thought check-ins · grounding tools · a daily care plan · when to reach out",
    listen: "https://www.youtube.com/watch?v=Kb24RrHIbFk",
    price: "14.99",
    was: "20.99",
    accent: "text-amber-300",
    card: "bg-[#17110a]/80 border-amber-500/30 hover:border-amber-400/60 hover:bg-[#1d150c]/90",
    listenBtn: "border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/25 text-amber-100",
    pdfBtn: "bg-amber-500/25 hover:bg-amber-500/45 text-amber-50",
  }],
  ["hardship", {
    shopId: "when_life_changes",
    kicker: "When Life Changes · Hardship",
    song: "Everybody Hurts",
    artist: "R.E.M.",
    album: "Automatic for the People",
    cover: "https://archive.org/download/mbid-0712fd61-a60e-42b8-ae88-d96f4f834d17/mbid-0712fd61-a60e-42b8-ae88-d96f4f834d17-7646243169_thumb500.jpg",
    coverAlt: "R.E.M. — Automatic for the People album cover",
    coverClass: "grayscale",
    blurb: "For loss, grief, and the seasons that knock you flat. You're not weak for hurting. Everybody hurts. Hold on.",
    inside: "what you're carrying · grief & loss · one day at a time · asking for help · small wins",
    listen: "https://www.youtube.com/watch?v=5rOiW_xY-kc",
    price: "14.99",
    was: "20.99",
    accent: "text-white",
    card: "bg-black border-white/25 hover:border-white/60 hover:bg-[#0a0a0a]",
    listenBtn: "border-white/40 bg-white/5 hover:bg-white/15 text-white",
    pdfBtn: "bg-white text-black hover:bg-neutral-200",
  }],
];

const ComingSoon = () => {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const { user, signup } = useAuth();
  const [loading, setLoading] = useState(false);
  const [entered, setEntered] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signup(email.trim(), password, name.trim() || null);
      toast.success("Welcome to Why We Recover.");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.toString() || "Could not create your account. Try again.");
      setLoading(false);
    }
  };

  return (
    <>
      {/* Entry splash — one-tap gesture lets background music begin */}
      {!entered && (
        <div
          data-testid="entry-splash"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-[#06090c] px-6"
        >
          <div className="absolute inset-0 pointer-events-none opacity-60">
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#06090c]" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-teal-500/10 blur-3xl" />
          </div>
          <div className="relative text-center max-w-md">
            <div className="mb-10 flex justify-center">
              <PhoenixImage variant="mark" size={64} />
            </div>
            <div className="text-xs uppercase tracking-[0.32em] text-teal-300/80 mb-4">
              Why We Recover
            </div>
            <h1 className="font-display text-3xl md:text-4xl text-neutral-100 leading-tight mb-6">
              Breathe. Then begin.
            </h1>
            <p className="text-sm text-neutral-400 leading-relaxed mb-10">
              Press enter and let the music carry you in.
            </p>
            <button
              type="button"
              data-testid="entry-splash-enter"
              onClick={() => setEntered(true)}
              className="btn-primary px-10 py-3 inline-flex items-center justify-center gap-2 text-sm uppercase tracking-[0.24em]"
            >
              Enter <ArrowRight size={14} />
            </button>
            <p className="text-[10px] text-neutral-600 mt-8 tracking-wider uppercase">
              Audio can be muted anytime
            </p>
          </div>
        </div>
      )}

      {/* Background music — starts after Enter gesture */}
      {entered && (
        <AmbientPlayer
          tracks={AMBIENT_TRACKS}
          autoplay
          volume={20}
        />
      )}

      <div className="fade-in min-h-screen">
      {/* Top bar */}
      <header className="absolute top-0 left-0 right-0 z-10">
        <LaunchBanner />
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-6">
          <div className="flex items-center gap-3">
            <PhoenixImage variant="mark" size={40} />
            <div className="leading-tight">
              <div className="font-display text-lg text-neutral-100">Why We Recover</div>
              <div className="hidden sm:block text-[9px] tracking-[0.28em] text-teal-300/80 uppercase mt-0.5">
                The pre-therapy standard
              </div>
            </div>
          </div>
          <nav className="flex items-center gap-4 md:gap-7 text-sm" data-testid="home-top-menu">
            {[["Story", "/story"], ["Studio", "/studio"], ["Soundtrack", "/soundtrack/preview"]].map(([l, to]) => (
              <Link key={to} to={to} data-testid={`home-nav-${l.toLowerCase()}`} className="hidden md:inline text-neutral-300 hover:text-teal-200 transition-colors">{l}</Link>
            ))}
            <Link to="/book" data-testid="home-nav-book" className="hidden sm:inline-flex whitespace-nowrap rounded-full border border-teal-400/50 px-4 py-2 text-teal-100 hover:bg-teal-500/10 transition-colors">Book a Session</Link>
            {user ? (
              <Link to="/dashboard" data-testid="home-nav-dashboard" className="btn-primary px-5 py-2 text-sm">My dashboard</Link>
            ) : (
              <>
                <Link to="/login" data-testid="home-nav-login" className="whitespace-nowrap text-neutral-300 hover:text-teal-200 transition-colors">Log in</Link>
                <Link to="/signup" data-testid="home-nav-signup" className="btn-primary px-5 py-2 text-sm whitespace-nowrap">Get started</Link>
              </>
            )}
          </nav>
        </div>
        <div className="md:hidden flex justify-center gap-6 text-xs text-neutral-300 -mt-2" data-testid="home-mobile-links">
          {[["Story", "/story"], ["Studio", "/studio"], ["Soundtrack", "/soundtrack/preview"], ["Book a Session", "/book"]].map(([l, to]) => (
            <Link key={to} to={to} data-testid={`home-mobile-${l.toLowerCase().replace(/ /g, "-")}`} className={`hover:text-teal-200 ${to === "/book" ? "sm:hidden text-teal-200" : ""}`}>{l}</Link>
          ))}
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden pt-48 sm:pt-40 pb-24">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-teal-300/80 mb-10">
            <span className="w-6 h-px bg-teal-400/60" />
            Now open
            <span className="w-6 h-px bg-teal-400/60" />
          </div>

          <div className="relative mx-auto mb-12 w-full">
            <div className="absolute inset-0 rounded-full bg-teal-500/10 blur-3xl pointer-events-none" />
            <img
              src="https://customer-assets-7cd3h4nn.emergentagent.net/wingman/5af77ba5-af76-4e7d-a867-09ea45f0ab28/attachments/a9335cec89c44721a1fe9549693df338_D4E55813-AEF1-4F4F-90F3-D58BAAC40D63.png"
              alt=""
              className="relative w-full h-auto mx-auto block"
              data-testid="hero-phoenix"
            />
          </div>

          <h1 className="font-display text-6xl md:text-7xl leading-[1.02] mb-6">
            <span className="gradient-text">60 seconds.</span>
          </h1>
          <p className="font-display text-2xl md:text-3xl text-neutral-100 leading-tight mb-8">
            That's how long my heart stopped — and why this exists.
          </p>
          <p className="text-neutral-300 text-lg leading-relaxed max-w-2xl mx-auto">
            Addiction, mental illness, or a hardship that knocked you flat. You don't have to wait for a
            diagnosis, a therapist, or the "right time." Healing begins with one honest moment, and we're
            building the space for that moment.
          </p>
          <div className="mt-12" data-testid="choose-path">
            <div className="text-xs uppercase tracking-[0.28em] text-neutral-400 mb-4">Choose your path</div>
            <div className="flex flex-wrap justify-center gap-3">
              {PATHS.map(([id, label, target, cls]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => goTo(target)}
                  data-testid={`path-${id}`}
                  className={`rounded-full border px-6 py-3 text-sm inline-flex items-center gap-2 transition-colors ${cls}`}
                >
                  {label} <ArrowRight size={14} />
                </button>
              ))}
            </div>
            <Link to="/book" data-testid="hero-book-session" className="mt-6 inline-flex items-center gap-2 btn-primary px-7 py-3 text-sm">
              Book a Session <ArrowRight size={14} aria-hidden="true" />
            </Link>
            <div>
              <StartQuiz />
            </div>
          </div>
        </div>
      </section>

      {/* Story block */}
      <section
        className="max-w-3xl mx-auto px-6 pb-20 space-y-6 silver-shimmer font-story-cinzel"
        data-testid="founder-story"
      >
        <p>
          I had been drinking heavily for more than fifteen years. Eventually, my addiction brought me
          to a night that nearly became my last.
        </p>
        <p>
          I mixed alcohol with medication and suffered severe alcohol poisoning. I lost consciousness.
          By the time emergency help arrived, I was unresponsive — and then I stopped breathing.
        </p>
        <p>CPR began. Emergency measures were taken to bring me back.</p>
        <p>For over a minute, I was gone.</p>
        <p>There are moments that divide your life into before and after. That was mine.</p>
        <p>
          But recovery didn't happen in that moment. Being brought back to life and learning how to
          live again were two very different things.
        </p>
        <p>
          I had to face the addiction, the pain underneath it, the damage I had caused, and the parts
          of myself I had spent years trying to escape. Recovery meant taking responsibility for my
          past without allowing it to become the rest of my story.
        </p>
        <p>I survived that night.</p>
        <p>Then I had to learn how to survive everything that came after it.</p>
        <p>
          Today, I am sober. And Why We Recover exists because I know there are people sitting in that
          same darkness, wondering whether their life can ever become something different.
        </p>
        <p>It can.</p>
        <p>This is the space I wish had existed when I was trying to find my way out.</p>
        <p data-testid="story-broaden">
          And it isn't only for addiction. It's for anyone carrying mental illness, grief, or a hardship
          too heavy to name.
        </p>
        <p data-testid="founder-signature">— Gabi, Founder and Recovery Advocate</p>
      </section>

      {/* Feature image + crisis support line */}
      <section className="max-w-6xl mx-auto px-6 mb-20" data-testid="support-image-section">
        <p
          className="mb-6 text-center italic text-[#A8A9AD] text-base md:text-lg leading-relaxed max-w-3xl mx-auto"
          data-testid="chester-caption"
        >
          In memory of Chester Bennington — whose voice gave words to pain that many of us couldn't
          speak out loud. I was 12 years old when his music found me, and it never let go. This is for
          everyone who found themselves in his lyrics.
        </p>
        <p className="mb-6 text-center">
          <a
            href="https://youtu.be/-pAu40lToXQ?si=MlW4eTYfdlO7uuSB"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="chester-song-link"
            className="inline-flex items-center gap-2 rounded-full border border-teal-400/40 bg-teal-500/10 hover:bg-teal-500/20 hover:border-teal-300/60 text-teal-200 hover:text-teal-100 px-6 py-3 text-sm md:text-base font-medium transition-colors"
          >
            Listen on YouTube
          </a>
        </p>
        <img
          src="https://customer-assets-7cd3h4nn.emergentagent.net/wingman/5af77ba5-af76-4e7d-a867-09ea45f0ab28/attachments/4fe36dc2ecbb4841996bd5f7016870f5_4F6713F0-3F07-4B34-B592-C2D17296A15D.png"
          alt=""
          className="w-full h-auto rounded-2xl"
          data-testid="support-image"
        />
        <p
          className="mt-4 text-center text-sm text-white"
          data-testid="crisis-line"
        >
          If you are struggling right now, help is available. Call or text 988.
        </p>
      </section>

      {/* Music tagline — between the graphic and waitlist */}
      <section className="max-w-3xl mx-auto px-6 mb-20 text-center" data-testid="music-tagline-section">
        <p
          className="font-display text-xl md:text-2xl text-white leading-snug"
          data-testid="music-tagline"
        >
          Music didn't fix me. It reminded me I was worth fixing.
        </p>
      </section>

      {/* Founder card */}
      <section className="max-w-5xl mx-auto px-6 mb-20">
        <div className="card-surface overflow-hidden" data-testid="founder-card">
          {/* THEN / NOW photo pair */}
          <div className="grid grid-cols-2 gap-0">
            <div className="relative aspect-[4/5]">
              <img src={BEFORE_PHOTO} alt="Then" className="absolute inset-0 w-full h-full object-cover" data-testid="then-photo" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute top-4 left-4 text-xs uppercase tracking-[0.32em] text-white/90 bg-black/40 backdrop-blur-sm rounded-full px-3 py-1 border border-white/10" data-testid="then-label">
                Then
              </div>
            </div>
            <div className="relative aspect-[4/5]">
              <img src={AFTER_PHOTO} alt="Now" className="absolute inset-0 w-full h-full object-cover" data-testid="now-photo" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
              <div className="absolute top-4 left-4 text-xs uppercase tracking-[0.32em] text-teal-200 bg-black/40 backdrop-blur-sm rounded-full px-3 py-1 border border-teal-400/40" data-testid="now-label">
                Now
              </div>
            </div>
          </div>

          <div className="p-8 md:p-10">
            <div className="text-xs uppercase tracking-[0.28em] text-teal-300/80 mb-3">Founder · Recovery Advocate</div>
            <h2 className="font-display text-4xl md:text-5xl leading-tight mb-4">Gabi</h2>
            <p className="text-neutral-300 leading-relaxed mb-6">
              I'm not a therapist. I won't diagnose you. What I am is someone who was gone for a minute
              and came back — and I'm building the tools I wish I'd had between the decision to change
              and the first appointment.
            </p>
            <blockquote className="border-l-4 border-teal-400/70 pl-4 py-2">
              <p className="font-display text-lg text-neutral-100 italic leading-snug">
                Tell the story of the mountain you climbed. Your words could become a page in someone
                else's survival guide.
              </p>
              <footer className="mt-3 text-xs tracking-widest uppercase text-teal-300/80">— Morgan Harper Nichols</footer>
            </blockquote>
          </div>
        </div>
      </section>

      {/* Artemis — emotional support animal tribute */}
      <section className="max-w-3xl mx-auto px-6 mb-20" data-testid="artemis-section">
        <p
          className="mb-8 text-center italic text-[#A8A9AD] text-base md:text-lg leading-relaxed"
          data-testid="artemis-caption"
        >
          "I am honoring Artemis's place in my journey and the love that comes with the support of having
          to take care of another life other than yours."
        </p>
        <img
          src="https://customer-assets-jt897jd0.emergentagent.net/job_why-recover-app/artifacts/b31e46a3d524c33e_82A0FC2A-4928-415B-971F-F1BC5F47D74B.png"
          alt="Artemis, Gabi's rescue and emotional support animal: The Color He Brought Into My Darkness"
          className="w-full h-auto rounded-2xl shadow-2xl"
          loading="lazy"
          data-testid="artemis-poster"
        />
        <RecoveryMoments />
      </section>

      {/* Free downloads — Begin Here */}
      <section id="free-downloads" className="max-w-6xl mx-auto px-6 mb-20" data-testid="free-downloads">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-neutral-400 mb-3">
            <span className="w-6 h-px bg-neutral-500/60" />
            Begin here
            <span className="w-6 h-px bg-neutral-500/60" />
          </div>
          <h2 className="font-display text-3xl md:text-4xl leading-tight text-neutral-100">
            Addiction: three levels, one step at a time.
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* 1 · First Steps — Come As You Are (Dark silver) */}
          <div
            data-testid="card-first-steps"
            className="relative rounded-2xl p-6 md:p-7 pt-10 bg-[#0f1518]/80 border border-[#A8A9AD]/30 hover:border-[#A8A9AD]/60 hover:bg-[#141a1e]/90 transition-colors flex flex-col"
          >
            <LaunchRibbon id="first-steps" />
            <div className="text-[10px] uppercase tracking-[0.28em] text-neutral-300 mb-3 inline-flex items-center gap-2" data-testid="level-first-steps"><span className="rounded-full border border-white/20 px-2 py-0.5">Level 1 · Beginner</span></div>
            <div className="flex gap-4 mb-4">
              <img
                src="https://upload.wikimedia.org/wikipedia/en/b/b7/NirvanaNevermindalbumcover.jpg"
                alt="Nirvana — Nevermind album cover"
                className="w-20 h-20 rounded-md object-cover flex-shrink-0 shadow-lg"
                data-testid="cover-nevermind"
              />
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#A8A9AD] mb-1">First Steps</div>
                <h3 className="font-display text-2xl text-neutral-100 leading-tight">Come As You Are</h3>
                <p className="text-xs text-neutral-500 mt-1">Nirvana · <em>Nevermind</em></p>
              </div>
            </div>
            <p className="text-sm text-neutral-300 leading-relaxed mb-3">
              A gentle starter workbook for anyone beginning. No diagnosis, no perfect plan — just one small step.
            </p>
            <p className="text-xs text-neutral-400 leading-relaxed mb-5 flex-1">
              <span className="text-[#A8A9AD]">Small anchors inside:</span> a glass of water · a 2-minute walk · three slow breaths · a text to one safe person
            </p>
            <RatingBadge shopId="first_steps" />
            <PriceTag id="first-steps" price="14.99" was={"20.99"} accent="text-[#A8A9AD]" />
            <div className="flex gap-2">
              <a
                href="https://www.youtube.com/watch?v=vabnZ9-ex7o"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="listen-first-steps"
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-[#A8A9AD]/40 bg-[#A8A9AD]/5 hover:bg-[#A8A9AD]/15 text-neutral-100 text-xs md:text-sm px-4 py-2.5 transition-colors"
              >
                <Play size={12} /> Listen
              </a>
              <button type="button" onClick={() => addToBundle("first_steps")} data-testid="buy-first-steps" className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#A8A9AD]/20 hover:bg-[#A8A9AD]/35 text-neutral-100 text-xs md:text-sm px-4 py-2.5 transition-colors"><ShoppingBag size={12} /> Buy</button>
            </div>
          </div>

          {/* 2 · Building Steadiness — Unsteady (Dark blue) */}
          <div
            data-testid="card-building-steadiness"
            className="relative rounded-2xl p-6 md:p-7 pt-10 bg-[#0b1424]/80 border border-[#3B5A8C]/40 hover:border-[#5B7FB8]/70 hover:bg-[#0f1a2e]/90 transition-colors flex flex-col"
          >
                        <div className="text-[10px] uppercase tracking-[0.28em] text-neutral-300 mb-3 inline-flex items-center gap-2" data-testid="level-building-steadiness"><span className="rounded-full border border-white/20 px-2 py-0.5">Level 2 · Intermediate</span></div>
            <div className="flex gap-4 mb-4">
              <img
                src="https://coverartarchive.org/release/89b9a362-af9b-4a19-8e56-647355a73f39/front-500"
                alt="X Ambassadors — VHS album cover"
                className="w-20 h-20 rounded-md object-cover flex-shrink-0 shadow-lg"
                data-testid="cover-vhs"
              />
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#8BA6D6] mb-1">Building Steadiness</div>
                <h3 className="font-display text-2xl text-neutral-100 leading-tight">Unsteady</h3>
                <p className="text-xs text-neutral-500 mt-1">X Ambassadors · <em>VHS</em></p>
              </div>
            </div>
            <p className="text-sm text-neutral-300 leading-relaxed mb-3">
              For when you've started changing and need structure. Map the loops, meet urges with a plan, hold through the setbacks.
            </p>
            <p className="text-xs text-neutral-400 leading-relaxed mb-5 flex-1">
              <span className="text-[#8BA6D6]">Inside:</span> mapping the cycle · urge plans · facts vs. story · boundaries · your next two weeks
            </p>
            <RatingBadge shopId="building_steadiness" />
            <PriceTag id="building-steadiness" price="20.99" was={null} accent="text-[#8BA6D6]" />
            <div className="flex gap-2">
              <a
                href="https://www.youtube.com/watch?v=V0lw3qylVfY"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="listen-building-steadiness"
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-[#3B5A8C]/50 bg-[#3B5A8C]/10 hover:bg-[#3B5A8C]/25 text-blue-100 text-xs md:text-sm px-4 py-2.5 transition-colors"
              >
                <Play size={12} /> Listen
              </a>
              <button type="button" onClick={() => addToBundle("building_steadiness")} data-testid="buy-building-steadiness" className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#3B5A8C]/30 hover:bg-[#3B5A8C]/50 text-blue-50 text-xs md:text-sm px-4 py-2.5 transition-colors"><ShoppingBag size={12} /> Buy</button>
            </div>
          </div>

          {/* 3 · More Support — Heavy (Teal · Chester continuity) */}
          <div
            data-testid="card-more-support"
            className="relative rounded-2xl p-6 md:p-7 pt-10 bg-[#06161a]/80 border border-teal-500/30 hover:border-teal-400/60 hover:bg-[#071d22]/90 transition-colors flex flex-col"
          >
                        <div className="text-[10px] uppercase tracking-[0.28em] text-neutral-300 mb-3 inline-flex items-center gap-2" data-testid="level-more-support"><span className="rounded-full border border-white/20 px-2 py-0.5">Level 3 · Advanced</span></div>
            <div className="flex gap-4 mb-4">
              <img
                src="https://onlyrocks.rocks/wp-content/uploads/2024/05/LINKIN-PARK-ONE-MORE-LIGHT-Seventh-Studio-Album-COVER.jpg"
                alt="Linkin Park — One More Light album cover"
                className="w-20 h-20 rounded-md object-cover flex-shrink-0 shadow-lg"
                data-testid="cover-one-more-light"
              />
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.28em] text-teal-300 mb-1">When Everything Feels Too Much</div>
                <h3 className="font-display text-2xl text-neutral-100 leading-tight">Heavy</h3>
                <p className="text-xs text-neutral-500 mt-1">Linkin Park · <em>One More Light</em></p>
              </div>
            </div>
            <p className="text-sm text-neutral-300 leading-relaxed mb-3">
              For the days the weight is too much to carry alone. A support-planning workbook — build your 10-minute plan, your people, your way through.
            </p>
            <p className="text-xs text-neutral-400 leading-relaxed mb-5 flex-1">
              <span className="text-teal-300">Inside:</span> the next 10 minutes · early warning signs · crisis contacts · what to tell your clinician
            </p>
            <RatingBadge shopId="more_support" />
            <PriceTag id="more-support" price="25.99" was={null} accent="text-teal-300" />
            <div className="flex gap-2">
              <a
                href="https://youtu.be/-pAu40lToXQ?si=MlW4eTYfdlO7uuSB"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="listen-more-support"
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-teal-500/40 bg-teal-500/10 hover:bg-teal-500/25 text-teal-100 text-xs md:text-sm px-4 py-2.5 transition-colors"
              >
                <Play size={12} /> Listen
              </a>
              <button type="button" onClick={() => addToBundle("more_support")} data-testid="buy-more-support" className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-teal-500/25 hover:bg-teal-500/45 text-teal-50 text-xs md:text-sm px-4 py-2.5 transition-colors"><ShoppingBag size={12} /> Buy</button>
            </div>
          </div>
        </div>

        <p className="text-center text-xs text-neutral-500 mt-6">
          Educational support, not clinical care. If you are in crisis, call or text <span className="text-white">988</span>.
        </p>
      </section>

      {/* Beyond addiction — mental health & hardship */}
      <section className="max-w-6xl mx-auto px-6 mb-20" data-testid="beyond-addiction">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-neutral-400 mb-3">
            <span className="w-6 h-px bg-neutral-500/60" />
            Beyond addiction
            <span className="w-6 h-px bg-neutral-500/60" />
          </div>
          <h2 className="font-display text-3xl md:text-4xl leading-tight text-neutral-100">
            Mental health &amp; hardship.
          </h2>
          <p className="text-neutral-400 text-sm md:text-base mt-3 max-w-xl mx-auto">
            Not every fight has a name like addiction. Some are in the mind. Some are just life, hitting hard.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {BEYOND_TOOLKITS.map(([id, t]) => (
            <ToolkitSongCard key={id} id={id} t={t} />
          ))}
        </div>
      </section>

      {/* Shop — bundle builder + 1-on-1 sessions */}
      <section id="shop" className="max-w-6xl mx-auto px-6 mb-20" data-testid="shop-section">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 min-w-0"><BundleBuilder /></div>
          <div className="lg:col-span-2 min-w-0"><SessionBooking /></div>
        </div>
      </section>

      <Testimonials />

      {/* Waitlist signup */}
      <section className="max-w-2xl mx-auto px-6 pb-24">
        <div className="card-surface p-8 md:p-10 relative overflow-hidden" data-testid="join-card">
          <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-teal-500/15 blur-3xl pointer-events-none" />
          <div className="relative">
            <div className="text-xs uppercase tracking-[0.28em] text-teal-300/80 mb-4">Free account</div>
            <h2 className="font-display text-3xl md:text-4xl leading-tight mb-3">
              <span className="text-neutral-100">Healing doesn't wait. </span>
              <span className="gradient-text">Join Why We Recover.</span>
            </h2>
            <p className="text-neutral-400 leading-relaxed mb-8">
              For recovery from addiction, mental illness, and life's hardest seasons. Your free account opens
              the Studio, your journal, the 60-Second Pivot and your free First 72 Hours toolkit.
            </p>
            {user ? (
              <Link to="/dashboard" data-testid="join-go-dashboard" className="btn-primary w-full py-3 inline-flex items-center justify-center gap-2">
                Go to my dashboard <ArrowRight size={16} />
              </Link>
            ) : (
              <form onSubmit={submit} className="space-y-3" data-testid="join-form">
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name (optional)"
                  aria-label="Your name" data-testid="join-name" className="wwr-input w-full rounded-xl px-4 py-3 text-sm" />
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com"
                  aria-label="Email" data-testid="join-email" className="wwr-input w-full rounded-xl px-4 py-3 text-sm" />
                <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create a password (6+ characters)" aria-label="Password" data-testid="join-password"
                  className="wwr-input w-full rounded-xl px-4 py-3 text-sm" />
                <button type="submit" disabled={loading} data-testid="join-submit"
                  className="btn-primary w-full py-3 inline-flex items-center justify-center gap-2 disabled:opacity-60">
                  {loading ? "Creating your account…" : (<>Join Why We Recover <ArrowRight size={16} /></>)}
                </button>
                <p className="text-xs text-neutral-500 text-center">
                  Already a member? <Link to="/login" className="text-teal-300 hover:text-teal-200" data-testid="join-login-link">Log in</Link>
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Minimal footer */}
      <footer className="border-t border-teal-500/15 bg-[#0a1418]/60">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col md:flex-row gap-4 md:items-center md:justify-between text-sm text-neutral-400">
          <div className="flex items-center gap-3">
            <PhoenixImage variant="mark" size={32} />
            <span className="text-[10px] tracking-[0.28em] text-teal-300/80 uppercase">
              Inspiring strength · Empowering recovery
            </span>
          </div>
          <div className="flex flex-col md:flex-row gap-2 md:gap-6 md:items-center">
            <a href="tel:+13604091672" className="inline-flex items-center gap-2 hover:text-teal-200">
              <Phone size={12} className="text-teal-300" /> 360-409-1672
            </a>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
};

export default ComingSoon;
